import React, { useState } from "react";
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, TextInput } from "react-native";
import { observer } from "mobx-react";
import { postStore } from "../stores/PostStore";
import { navigate } from "../services/NavigationService";
import { EnumRoutesAppWeb } from "../enum/EnumRoutesAppWeb";
import { PlusIcon, PencilSimpleIcon, TrashIcon, FunnelSimple as FilterIcon, MagnifyingGlass as SearchIcon } from "@phosphor-icons/react";
import { Theme } from "../enum/Theme";
import { Language } from "@constants/language";
import { ModalFiltroLanguage } from "../comps/ModalFiltroLanguage";
import { ModalFiltroAgeRange } from "../comps/ModalFiltroAgeRange";
import SpaceW from "@comps/SpaceW";
import { getFlagByLanguage } from "../services/getFlagByLanguage";

const Blog = observer(() => {
  const { posts, loading, deletePost } = postStore;

  const [modalLangVisible, setModalLangVisible] = useState(false);
  const [modalAgeVisible, setModalAgeVisible] = useState(false);
  const [filterLang, setFilterLang] = useState<Language | null>(null);
  const [filterAges, setFilterAges] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  // Filtro combinado: idioma + múltiplas idades + busca
  const postsFiltered = posts.filter((post) => {
    // Filtro por idioma
    const langOk = filterLang ? post.lang === filterLang : true;
    
    // Filtro por idade
    const ageOk = filterAges.length > 0
      ? post.ageRanges && post.ageRanges.some((age: string) => filterAges.includes(age))
      : true;
    
    // Filtro por busca (título, resumo e texto completo)
    const searchOk = !searchText || 
      (post.title && post.title.toLowerCase().includes(searchText.toLowerCase())) ||
      (post.summary && post.summary.toLowerCase().includes(searchText.toLowerCase())) ||
      (post.fullText && post.fullText.toLowerCase().includes(searchText.toLowerCase()));
    
    return langOk && ageOk && searchOk;
  });


  if (loading) {
    return <Text style={{ textAlign: "center", marginTop: 50 }}>Carregando...</Text>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.topBar}>
        <TouchableOpacity style={styles.addButton} onPress={() => navigate(EnumRoutesAppWeb.createEditBlog)}>
          <PlusIcon size={20} color="#fff" />
          <Text style={styles.addButtonText}>Novo Artigo</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.filterButton} onPress={() => setModalLangVisible(true)}>
          <FilterIcon size={22} color={Theme.foreground} />
          <Text style={{ marginLeft: 5 }}>Idioma</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.filterButton} onPress={() => setModalAgeVisible(true)}>
          <FilterIcon size={22} color={Theme.foreground} />
          <Text style={{ marginLeft: 5 }}>Idade</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <SearchIcon size={20} color="#666" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Buscar por título, resumo ou conteúdo..."
            value={searchText}
            onChangeText={setSearchText}
            placeholderTextColor="#999"
          />
        </View>
      </View>

      <ScrollView>
        {postsFiltered.map((post) => (
          <View style={styles.row} key={post.id}>
            <View>
              <Text style={{ fontSize: 18 }}>{getFlagByLanguage(post.lang)}</Text>
            </View>
            <SpaceW />
            <Text style={styles.rowText}>{post.title || "-"}</Text>
            <SpaceW />
            <View>
              {post.ageRanges && post.ageRanges.length > 0 &&
                <Text style={{ fontSize: 12, color: "#888" }}>
                  {post.ageRanges.map((age: string) => `${age}a`).join(", ")}
                </Text>
              }
            </View>
            <SpaceW />
            <View style={styles.actions}>
              <TouchableOpacity onPress={() => navigate(EnumRoutesAppWeb.createEditBlog, { post })}>
                <PencilSimpleIcon size={20} color="#555" />
              </TouchableOpacity>
              <SpaceW />
              <TouchableOpacity onPress={() => deletePost(post.id)}>
                <TrashIcon size={20} color="#E57373" />
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>

      <ModalFiltroLanguage
        visible={modalLangVisible}
        value={filterLang}
        onSelect={setFilterLang}
        onClose={() => setModalLangVisible(false)}
      />

      <ModalFiltroAgeRange
        visible={modalAgeVisible}
        value={filterAges}
        onSelect={setFilterAges}
        onClose={() => setModalAgeVisible(false)}
      />
    </View>
  );
});

export default Blog;

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  topBar: { flexDirection: "row", alignItems: "center", marginBottom: 15 },
  addButton: { backgroundColor: Theme.foreground, padding: 10, borderRadius: 5, flexDirection: "row", alignItems: "center" },
  addButtonText: { color: "#fff", marginLeft: 5 },
  filterButton: { marginLeft: 10, padding: 7, borderRadius: 5, backgroundColor: "#e6e6e6", flexDirection: "row", alignItems: "center" },
  searchContainer: { marginBottom: 15 },
  searchInputContainer: { 
    flexDirection: "row", 
    alignItems: "center", 
    backgroundColor: "#fff", 
    borderRadius: 8, 
    paddingHorizontal: 12, 
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#ddd"
  },
  searchIcon: { marginRight: 8 },
  searchInput: { 
    flex: 1, 
    fontSize: 16, 
    color: "#333",
    paddingVertical: 4
  },
  row: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: "#ccc" },
  rowText: { flex: 2 },
  actions: { flexDirection: "row", gap: 10 },
});
